import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import React from 'react'
import {
  AdopterDto,
  AdoptionAttemptDtoStatus,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoStep,
  AnimalDto,
} from '../../../../interactors/gen/backendClient'
import dayjs from 'dayjs'
import { LoadingButton } from '@mui/lab'
import { AnimalsClient } from '../../../../interactors/clients/AnimalsClient'
import { useGlobalSnackbarStore } from '../../../../store/GlobalSnackBarStore'
import { AdoptersClient } from '../../../../interactors/clients/AdoptersClient'
import { useNavigate } from 'react-router-dom'

interface Props {
  isOpen: boolean
  onClose: () => void
  animal: AnimalDto
  adopter: AdopterDto
  shouldRedirect?: boolean
}

export const VerdictAfterVPAModal: React.FC<Props> = ({ isOpen, onClose, animal, adopter, shouldRedirect = false }) => {
  const [validateLoading, setValidateLoading] = React.useState(false)
  const [cancelLoading, setCancelLoading] = React.useState(false)

  const globalSnackBarStore = useGlobalSnackbarStore()
  const navigate = useNavigate()
  const vpaDateDisplay = animal.adoption?.vpaDate
    ? dayjs(animal.adoption.vpaDate).format('DD/MM/YYYY')
    : 'Date inconnue'

  const onValidate = async () => {
    setValidateLoading(true)
    animal.adoption.step = AnimalAdoptionDtoStep.AdoptionValidatedAfterPostVisit
    await AnimalsClient.editAnimal(animal).then((data) => {
      animal = data
    })
    await endAdoptionAttempt()
    globalSnackBarStore.triggerSuccessMessage('La visite post-adoption a bien été validée.')
    setValidateLoading(false)
    onClose()
    if (shouldRedirect) {
      const adoptionAttempt = adopter.adoptionAttempts?.find(
        (adoptionAttempt) => adoptionAttempt.animalId === animal.id
      )
      navigate(`/suivi-adoptant/${adopter.id}?tab=past-adoption&attemptId=${adoptionAttempt?.id}`)
    } else {
      window.location.reload()
    }
  }

  const onCancel = async () => {
    setCancelLoading(true)
    await setAnimalToAdoptableStatus()
    await suspendAdoptionAttempt()
    globalSnackBarStore.triggerSuccessMessage("L'adoption a bien été annulée.")
    setCancelLoading(false)
    onClose()
    if (shouldRedirect) {
      const adoptionAttempt = adopter.adoptionAttempts?.find(
        (adoptionAttempt) => adoptionAttempt.animalId === animal.id
      )
      navigate(`/suivi-adoptant/${adopter.id}?tab=suspended-adoption&attemptId=${adoptionAttempt?.id}`)
    } else {
      window.location.reload()
    }
  }

  const setAnimalToAdoptableStatus = async () => {
    animal.adoption.status = AnimalAdoptionDtoStatus.Adoptable
    animal.adoption.step = undefined
    animal.adoption.adopterId = undefined
    animal.adoption.vpaDate = undefined
    animal.adoption.exitDate = undefined
    animal.adoption.adoptionDate = undefined
    await AnimalsClient.editAnimal(animal).then((data) => {
      animal = data
    })
  }

  const suspendAdoptionAttempt = async () => {
    const adoptionAttempt = adopter.adoptionAttempts?.find((adoptionAttempt) => adoptionAttempt.animalId === animal.id)
    if (adoptionAttempt) {
      adoptionAttempt.status = AdoptionAttemptDtoStatus.SuspendedByCustomer
      await AdoptersClient.editAccount(adopter).then((data) => {
        adopter = data
      })
    }
  }

  const endAdoptionAttempt = async () => {
    const adoptionAttempt = adopter.adoptionAttempts?.find((adoptionAttempt) => adoptionAttempt.animalId === animal.id)
    if (adoptionAttempt) {
      adoptionAttempt.step = 6
      adoptionAttempt.status = AdoptionAttemptDtoStatus.Done
      await AdoptersClient.editAccount(adopter).then((data) => {
        adopter = data
      })
    }
  }
  return (
    <Dialog
      maxWidth="sm"
      open={isOpen}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: '1px solid #E4E8F2',
          boxShadow: '0 18px 42px rgba(20, 32, 60, 0.12)',
          overflow: 'hidden',
        },
      }}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
    >
      <DialogTitle sx={{ pb: 1.25, fontWeight: 800, color: '#203864' }}>Verdict de la visite post-adoption</DialogTitle>

      <Box
        sx={{
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
          boxSizing: 'content-box',
        }}
      >
        <DialogContent sx={{ overflowY: 'scroll', maxHeight: 'calc(100vh - 230px)', pt: 0.5 }}>
          <Box
            sx={{
              borderRadius: 3,
              backgroundColor: '#F4F7FD',
              border: '1px solid #DCE5F4',
              px: 2,
              py: 1.75,
            }}
          >
            <Typography sx={{ color: '#203864', fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.35 }}>
              Vous avez effectué la visite post-adoption le {vpaDateDisplay}.
            </Typography>
            <Typography sx={{ mt: 0.75, color: '#5D6785', fontSize: '0.95rem', fontWeight: 600 }}>
              Merci de confirmer votre décision.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 3,
            pb: 2.5,
            pt: 1.5,
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            type="button"
            onClick={onClose}
            disabled={cancelLoading || validateLoading}
            sx={{
              minHeight: 44,
              px: 2,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 800,
              borderColor: '#E7C56C',
              color: '#D89B00',
              '&:hover': {
                borderColor: '#DDB250',
                backgroundColor: 'rgba(255, 196, 64, 0.06)',
              },
            }}
          >
            Plus tard
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <LoadingButton
              variant="contained"
              color="error"
              type="button"
              onClick={onCancel}
              disabled={validateLoading}
              loading={cancelLoading}
              sx={{
                minHeight: 44,
                px: 2.25,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 800,
                boxShadow: 'none',
                backgroundColor: '#D96A5D',
                '&:hover': {
                  backgroundColor: '#C95C50',
                },
              }}
            >
              Annuler l&#39;adoption
            </LoadingButton>
            <LoadingButton
              variant="contained"
              type="button"
              onClick={onValidate}
              disabled={cancelLoading}
              loading={validateLoading}
              sx={{
                minHeight: 44,
                px: 2.25,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 800,
                boxShadow: 'none',
                backgroundColor: '#5E88C7',
                '&:hover': {
                  backgroundColor: '#4E79BA',
                },
              }}
            >
              Valider la VPA
            </LoadingButton>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
